"use client";

import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { useScrollDirection } from "@/lib/hooks";

export interface ScrollHideWrapperProps {
  /** Content to be wrapped */
  children: React.ReactNode;
  /** Minimum scroll distance before direction changes (default: 10) */
  threshold?: number;
  /** Vertical offset when hidden in pixels (default: -100) */
  hideOffset?: number;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * Wrapper component that hides content when scrolling down and shows it when scrolling up
 *
 * Uses motion.div for smooth GPU-accelerated animations. Ideal for sticky headers
 * and navigation bars that should hide during downward scrolling.
 *
 * @example
 * ```tsx
 * <ScrollHideWrapper>
 *   <Navigation />
 * </ScrollHideWrapper>
 * ```
 */
export const ScrollHideWrapper: React.FC<ScrollHideWrapperProps> = ({
  children,
  threshold = 10,
  hideOffset = -100,
  className = "",
}) => {
  const { direction, isAtTop } = useScrollDirection({ threshold });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleMobileMenuState = (e: Event) => {
      setIsMobileMenuOpen(
        (e as CustomEvent<{ isOpen: boolean }>).detail.isOpen
      );
    };
    window.addEventListener("mobile-menu-state", handleMobileMenuState);
    return () =>
      window.removeEventListener("mobile-menu-state", handleMobileMenuState);
  }, []);

  const translateY =
    isMobileMenuOpen || direction !== "down" || isAtTop ? 0 : hideOffset;

  return (
    <motion.div
      className={className}
      animate={{ y: translateY }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
      }}
    >
      {children}
    </motion.div>
  );
};
